/**
 * CR319: Details Content Accordion for Deal Cards → Structure tab
 * 
 * Collapsed accordion showing:
 * - Master enable toggle
 * - Drag-sortable list of detail sections with enabled toggles
 * - Max lines control per section (for terms/fine_print)
 * - Admin-only indicator for evidence/validator
 * 
 * Persists to template_json.details_content and updates preview immediately.
 */

import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  FileText,
  Info,
  Clock,
  Eye,
  ShieldCheck,
  Sparkles,
  RotateCcw,
  ListCollapse,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ================================================================
// TYPES
// ================================================================

export type DetailsSectionKey = 
  | "offer_terms"
  | "fine_print"
  | "source_last_checked"
  | "evidence"
  | "validator_results"
  | "actions";

export interface DetailsSectionSettings {
  enabled: boolean;
  max_lines?: number;
  admin_only?: boolean;
}

export interface DetailsContentConfig {
  enabled: boolean;
  order: DetailsSectionKey[];
  sections: Record<DetailsSectionKey, DetailsSectionSettings>;
}

export const DEFAULT_DETAILS_CONTENT_CONFIG: DetailsContentConfig = {
  enabled: true,
  order: ["offer_terms", "fine_print", "source_last_checked", "evidence", "validator_results", "actions"],
  sections: {
    offer_terms: { enabled: true, max_lines: 3 },
    fine_print: { enabled: true, max_lines: 2 },
    source_last_checked: { enabled: true },
    evidence: { enabled: true, admin_only: true },
    validator_results: { enabled: false, admin_only: true },
    actions: { enabled: true },
  },
};

/**
 * CR326 hotfix: Normalize a potentially null/partial config onto defaults.
 * Guarantees enabled, order, and sections are always defined.
 */
export function normalizeDetailsContentConfig(
  raw?: DetailsContentConfig | null | Partial<DetailsContentConfig>
): DetailsContentConfig {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_DETAILS_CONTENT_CONFIG };

  const defaults = DEFAULT_DETAILS_CONTENT_CONFIG;

  // Merge sections: default each section, then overlay any provided values
  const mergedSections = { ...defaults.sections };
  if (raw.sections && typeof raw.sections === "object") {
    for (const key of Object.keys(mergedSections) as DetailsSectionKey[]) {
      if (raw.sections[key]) {
        mergedSections[key] = { ...mergedSections[key], ...raw.sections[key] };
      }
    }
  }

  // Validate order array: must contain valid keys, fall back to default if broken
  let mergedOrder = defaults.order;
  if (Array.isArray(raw.order) && raw.order.length > 0) {
    const validKeys = new Set<string>(Object.keys(defaults.sections));
    const filtered = raw.order.filter((k): k is DetailsSectionKey => validKeys.has(k));
    if (filtered.length > 0) {
      // Add any missing keys from defaults
      const missing = defaults.order.filter(k => !filtered.includes(k));
      mergedOrder = [...filtered, ...missing];
    }
  }

  return {
    enabled: typeof raw.enabled === "boolean" ? raw.enabled : defaults.enabled,
    order: mergedOrder,
    sections: mergedSections,
  };
}

interface DetailsContentAccordionProps {
  config?: DetailsContentConfig | null;
  onChange: (config: DetailsContentConfig) => void;
  defaultOpen?: boolean;
  /** CR340: When true, content is always visible (no accordion expand/collapse) */
  alwaysExpanded?: boolean;
  className?: string;
}

// Section metadata
const SECTION_META: Record<DetailsSectionKey, { label: string; icon: React.ElementType; hasMaxLines: boolean; adminOnly?: boolean }> = {
  offer_terms: { label: "Offer Terms", icon: FileText, hasMaxLines: true },
  fine_print: { label: "Fine Print / Exclusions", icon: Info, hasMaxLines: true },
  source_last_checked: { label: "Source & Last Checked", icon: Clock, hasMaxLines: false },
  evidence: { label: "Evidence", icon: Eye, hasMaxLines: false, adminOnly: true },
  validator_results: { label: "Validator Results", icon: ShieldCheck, hasMaxLines: false, adminOnly: true },
  actions: { label: "Actions", icon: Sparkles, hasMaxLines: false },
};

// ================================================================
// COMPONENT
// ================================================================

export function DetailsContentAccordion({
  config: rawConfig,
  onChange,
  defaultOpen = false,
  alwaysExpanded = false,
  className,
}: DetailsContentAccordionProps) {
  // CR326 hotfix: always normalize to prevent crashes from null/partial config
  const config = useMemo(() => normalizeDetailsContentConfig(rawConfig), [rawConfig]);
  const [isOpen, setIsOpen] = useState(defaultOpen || alwaysExpanded);
  const [activeId, setActiveId] = useState<string | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handlers
  const handleMasterToggle = useCallback((enabled: boolean) => {
    onChange({ ...config, enabled });
  }, [config, onChange]);

  const handleSectionToggle = useCallback((key: DetailsSectionKey, enabled: boolean) => {
    onChange({
      ...config,
      sections: {
        ...config.sections,
        [key]: { ...config.sections[key], enabled },
      },
    });
  }, [config, onChange]);

  const handleMaxLinesChange = useCallback((key: DetailsSectionKey, maxLines: number) => {
    onChange({
      ...config,
      sections: {
        ...config.sections,
        [key]: { ...config.sections[key], max_lines: maxLines },
      },
    });
  }, [config, onChange]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = config.order.indexOf(active.id as DetailsSectionKey);
    const newIndex = config.order.indexOf(over.id as DetailsSectionKey);
    if (oldIndex === -1 || newIndex === -1) return;

    onChange({
      ...config,
      order: arrayMove(config.order, oldIndex, newIndex),
    });
  }, [config, onChange]);

  const handleReset = useCallback(() => {
    onChange(DEFAULT_DETAILS_CONTENT_CONFIG);
  }, [onChange]);

  // Count enabled sections
  const enabledCount = useMemo(() => {
    return Object.values(config.sections).filter(s => s.enabled).length;
  }, [config.sections]);

  // Check if order differs from default
  const hasCustomOrder = useMemo(() => {
    return config.order.some((k, i) => k !== DEFAULT_DETAILS_CONTENT_CONFIG.order[i]);
  }, [config.order]);

  return (
    <Collapsible open={alwaysExpanded || isOpen} onOpenChange={alwaysExpanded ? undefined : setIsOpen} className={className}>
      {alwaysExpanded ? (
        <div className="flex items-center justify-between w-full p-3 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-2">
            <ListCollapse className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Details Content</span>
            <span className="text-xs text-muted-foreground">
              ({enabledCount}/{Object.keys(config.sections).length})
            </span>
          </div>
          <Badge variant={config.enabled ? "secondary" : "outline"} className="text-[10px]">
            {config.enabled ? "On" : "Off"}
          </Badge>
        </div>
      ) : (
        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <ListCollapse className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Details Content</span>
            <span className="text-xs text-muted-foreground">
              ({enabledCount}/{Object.keys(config.sections).length})
            </span>
          </div>
          <Badge variant={config.enabled ? "secondary" : "outline"} className="text-[10px]">
            {config.enabled ? "On" : "Off"}
          </Badge>
        </CollapsibleTrigger>
      )}

      <CollapsibleContent className="pt-2 space-y-3">
        {/* Master toggle */}
        <div className="flex items-center justify-between px-3 py-2 border rounded-lg bg-card">
          <div>
            <Label className="text-sm font-normal">Enable Details Content</Label>
            <p className="text-xs text-muted-foreground">Show deal details sections</p>
          </div>
          <Switch
            checked={config.enabled}
            onCheckedChange={handleMasterToggle}
          />
        </div>

        {config.enabled && (
          <>
            {/* Order header */}
            <div className="flex items-center justify-between px-1">
              <Label className="text-xs text-muted-foreground">Section Order</Label>
              {hasCustomOrder && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="h-6 text-[10px] gap-1 px-2"
                >
                  <RotateCcw className="h-3 w-3" />
                  Reset
                </Button>
              )}
            </div>

            {/* Sortable section list */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={({ active }) => setActiveId(active.id as string)}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={config.order} strategy={verticalListSortingStrategy}>
                <div className="space-y-1">
                  {config.order.map((key) => (
                    <SortableSectionRow
                      key={key}
                      id={key}
                      meta={SECTION_META[key]}
                      settings={config.sections[key]}
                      onToggle={(enabled) => handleSectionToggle(key, enabled)}
                      onMaxLinesChange={(maxLines) => handleMaxLinesChange(key, maxLines)}
                    />
                  ))}
                </div>
              </SortableContext>

              <DragOverlay>
                {activeId && SECTION_META[activeId as DetailsSectionKey] && (
                  <div className="flex items-center gap-2 py-1.5 px-2 rounded-md border bg-background shadow-lg">
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{SECTION_META[activeId as DetailsSectionKey].label}</span>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ================================================================
// SORTABLE ROW
// ================================================================

interface SortableSectionRowProps {
  id: string;
  meta: { label: string; icon: React.ElementType; hasMaxLines: boolean; adminOnly?: boolean };
  settings: DetailsSectionSettings;
  onToggle: (enabled: boolean) => void;
  onMaxLinesChange: (maxLines: number) => void;
}

function SortableSectionRow({ id, meta, settings, onToggle, onMaxLinesChange }: SortableSectionRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = meta.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 py-1.5 px-2 rounded-md border bg-background transition-colors",
        isDragging && "opacity-50 border-primary/50 shadow-md",
        !settings.enabled && "opacity-60"
      )}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
      </button>

      {/* Icon */}
      <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />

      {/* Label */}
      <span className="flex-1 text-sm truncate">{meta.label}</span>

      {/* Admin badge */}
      {meta.adminOnly && (
        <Badge variant="outline" className="text-[8px] px-1 py-0 flex-shrink-0">
          Admin
        </Badge>
      )}

      {/* Max lines input (for terms/fine_print) */}
      {meta.hasMaxLines && settings.enabled && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Input
                type="number"
                min={1}
                max={10}
                value={settings.max_lines ?? 3}
                onChange={(e) => onMaxLinesChange(parseInt(e.target.value) || 3)}
                className="h-6 w-12 text-[10px] px-1.5"
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Max lines to display</p>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Toggle */}
      <Switch
        checked={settings.enabled}
        onCheckedChange={onToggle}
        className="flex-shrink-0"
      />
    </div>
  );
}

export default DetailsContentAccordion;
